import { FC, MouseEventHandler } from 'react';
import { useSelector } from 'react-redux';
import { graphql, createFragmentContainer } from 'react-relay/legacy';
import CheckCircle from 'dibs-icons/exports/legacy/CircleCheck';
import ReturnArrow from 'dibs-icons/exports/legacy/ReturnArrow';
import { ClientSuspense } from 'dibs-elements/exports/ClientSuspense';
import { dibsLazyClient } from 'dibs-lazy/exports/dibsLazyClient';

// components
import { PdpRespSidebarBlurbShipping } from './PdpRespSidebarBlurbShipping';
import { PdpRespSidebarBlurbPricingGuidance } from './PdpRespSidebarBlurbPricingGuidance';
import { PdpRespSidebarBlurbReturnPolicy } from './PdpRespSidebarBlurbReturnPolicy';
import { useRenderBlurbs } from './hooks/useRenderBlurbs';
import { useExposedReturnsClientVariant } from '../hooks/useExposedReturnsClientVariant';
import { RelayVariables, Store } from '../../global/relayVariableTypes';
import { PdpSharedBlurb } from '../PdpSharedBlurbs/PdpSharedBlurb/PdpSharedBlurb';

import PdpSharedSidebarBuyerIncentives from '../shared/PdpSharedSidebarBuyerIncentives/PdpSharedSidebarBuyerIncentives';
const PdpSharedSidebarItemReturns = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpSharedSidebarItemReturns" */ '../shared/PdpSharedSidebarItemReturns/PdpSharedSidebarItemReturns'
        )
);

import { PdpRespSidebarBlurbs_viewer$data } from './__generated__/PdpRespSidebarBlurbs_viewer.graphql';
import { PdpRespSidebarBlurbs_item$data } from './__generated__/PdpRespSidebarBlurbs_item.graphql';
import { PdpRespSidebarBlurbs_user$data } from './__generated__/PdpRespSidebarBlurbs_user.graphql';
import { PdpRespSidebarBlurbs_displayInfo$data } from './__generated__/PdpRespSidebarBlurbs_displayInfo.graphql';
import { PdpRespSidebarBlurbs_sku$data } from './__generated__/PdpRespSidebarBlurbs_sku.graphql';

// styles
import styles from './PdpRespSidebarBlurbs.scss';

type Props = {
    viewer: PdpRespSidebarBlurbs_viewer$data;
    item: PdpRespSidebarBlurbs_item$data;
    user: PdpRespSidebarBlurbs_user$data | null | undefined;
    displayInfo: PdpRespSidebarBlurbs_displayInfo$data | null | undefined;
    sku: PdpRespSidebarBlurbs_sku$data | null | undefined;
    currency?: string;
    itemQuantity?: number;
    isClient: boolean;
    buyerIncentivesSidebarClick: MouseEventHandler;
    handleRoundPriceTracking?: () => void;
};

export const PdpRespSidebarBlurbsComponent: FC<Props> = ({
    currency = 'USD',
    viewer,
    item,
    user,
    displayInfo = null,
    sku,
    itemQuantity,
    isClient,
    buyerIncentivesSidebarClick,
    handleRoundPriceTracking,
}) => {
    const renderBlurbs = useRenderBlurbs({ item, displayInfo });
    const relayVariables = useSelector<Store, RelayVariables>(
        state => state.relayVariables.variables
    );
    const isExposedReturnsClientVariant = useExposedReturnsClientVariant() && isClient;

    if (!renderBlurbs) {
        return null;
    }

    return (
        <div className={styles.container}>
            <PdpRespSidebarBlurbShipping
                currency={currency}
                item={item}
                viewer={viewer}
                user={user}
                renderTooltip
                sku={relayVariables.isMultiSku ? sku : null}
                isClient={relayVariables.isClient}
                itemQuantity={itemQuantity}
                shortLinkCta
                handleRoundPriceTracking={handleRoundPriceTracking}
            />

            {isClient && (
                <PdpRespSidebarBlurbPricingGuidance
                    viewer={viewer}
                    currency={currency}
                    item={item}
                    skuId={sku?.serviceId}
                />
            )}

            {isExposedReturnsClientVariant && (
                <ClientSuspense fallback={null}>
                    <PdpSharedBlurb
                        zeroPadding
                        IconComponent={ReturnArrow}
                        iconClass={styles.returnsIcon}
                    >
                        <PdpSharedSidebarItemReturns item={item} user={user} />
                    </PdpSharedBlurb>
                </ClientSuspense>
            )}

            <PdpSharedBlurb zeroPadding IconComponent={CheckCircle} iconClass={styles.icon}>
                <PdpSharedSidebarBuyerIncentives onDetailsClick={buyerIncentivesSidebarClick} />
            </PdpSharedBlurb>

            <div className={styles.returnPolicyContainer}>
                <PdpRespSidebarBlurbReturnPolicy item={item} />
            </div>
        </div>
    );
};

export const PdpRespSidebarBlurbs = createFragmentContainer(PdpRespSidebarBlurbsComponent, {
    viewer: graphql`
        fragment PdpRespSidebarBlurbs_viewer on Viewer {
            ...PdpRespSidebarBlurbShipping_viewer
            ...PdpRespSidebarBlurbPricingGuidance_viewer @include(if: $isClient)
        }
    `,
    item: graphql`
        fragment PdpRespSidebarBlurbs_item on Item {
            ...useRenderBlurbs_item
            ...PdpRespSidebarBlurbShipping_item
            ...PdpRespSidebarBlurbReturnPolicy_item
            ...PdpRespSidebarBlurbPricingGuidance_item @include(if: $isClient)
            ...PdpSharedSidebarItemReturns_item @include(if: $isClient)
        }
    `,
    user: graphql`
        fragment PdpRespSidebarBlurbs_user on User {
            serviceId
            ...PdpRespSidebarBlurbShipping_user
            ...PdpRespSidebarBlurbShipping_user
            ...PdpSharedSidebarItemReturns_user @include(if: $isClient)
        }
    `,
    displayInfo: graphql`
        fragment PdpRespSidebarBlurbs_displayInfo on ItemDisplayInfo {
            ...useRenderBlurbs_displayInfo
        }
    `,
    sku: graphql`
        fragment PdpRespSidebarBlurbs_sku on ItemSkuType {
            ... on ItemSkuType @include(if: $isMultiSku) {
                serviceId
                ...PdpRespSidebarBlurbShipping_sku
            }
        }
    `,
});
